import {
  Check,
  KeyRound,
  LoaderCircle,
  Plus,
  Search,
  Trash2,
  type LucideIcon
} from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient, type AiModel, type CopilotoSettings, type CopilotoStatus } from "@/shared/api/api-client";

import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { useConfirmLeave, useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";
type Tab = "prompts" | "quebra-gelos" | "credenciais";

function PromptCard({
  icon: Icon,
  title,
  description,
  value,
  onChange,
  onSave,
  saving,
  dirty
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}) {
  return (
    <div className="card copiloto-prompt-card">
      <div className="card-title">
        <div><Icon size={18} aria-hidden="true"/><h3>{title}</h3><p>{description}</p></div>
      </div>
      <textarea rows={10} value={value} onChange={event => onChange(event.target.value)}/>
      <div className="copiloto-admin-actions">
        <button className="primary-button" type="button" disabled={!dirty || saving} onClick={onSave}>
          {saving ? <LoaderCircle className="spin" size={16}/> : <Check size={16}/>} Salvar
        </button>
      </div>
    </div>
  );
}

function PromptsTab({
  token,
  settings,
  onSaved
}: {
  token: string;
  settings: CopilotoSettings;
  onSaved: (settings: CopilotoSettings) => void;
}) {
  const [promptPadrao, setPromptPadrao] = useState(settings.prompt_padrao);
  const [saving, setSaving] = useState(false);
  // Sem isto a falha era MUDA: o spinner parava, o botão voltava ao
  // normal e nada era dito — o admin concluía que tinha salvo. O
  // servidor recusa prompt acima do teto, e essa recusa precisa chegar
  // em alguém.
  const [saveError, setSaveError] = useState<string | null>(null);

  // Só o prompt do modo interno: o modo Web saiu em 2026-10-13, e o
  // `prompt_web` do servidor fica como está, sem tela.
  const save = async (): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await apiClient.updateCopilotoAdminSettings(
        token,
        { prompt_padrao: promptPadrao }
      );
      onSaved(updated);
      return true;
    } catch (error) {
      setSaveError(
        errorMessage(error, "Não foi possível salvar o prompt.")
      );
      return false;
    } finally {
      setSaving(false);
    }
  };
  useUnsavedChanges(promptPadrao !== settings.prompt_padrao, save, "o prompt de sistema");

  return (
    <div className="copiloto-admin-grid">
      <PromptCard
        icon={Search}
        title="Prompt de sistema"
        description="Instruções que a Mestra Help segue em toda conversa."
        value={promptPadrao}
        onChange={setPromptPadrao}
        onSave={() => void save()}
        saving={saving}
        dirty={promptPadrao !== settings.prompt_padrao}
      />
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
    </div>
  );
}

function IcebreakersTab({
  token,
  settings,
  onSaved
}: {
  token: string;
  settings: CopilotoSettings;
  onSaved: (settings: CopilotoSettings) => void;
}) {
  const [items, setItems] = useState<string[]>(settings.icebreakers);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = JSON.stringify(items) !== JSON.stringify(settings.icebreakers);

  const updateItem = (index: number, value: string) =>
    setItems(current => current.map((item, i) => (i === index ? value : item)));
  const removeItem = (index: number) => setItems(current => current.filter((_, i) => i !== index));
  const addItem = () => setItems(current => [...current, ""]);

  const save = async (): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);
    try {
      const filtered = items.map(item => item.trim()).filter(Boolean);
      const updated = await apiClient.updateCopilotoAdminSettings(token, { icebreakers: filtered });
      // A lista vem do servidor, que apara e limita — reexibir o
      // resultado é o que faz a coerção ser visível em vez de virar
      // divergência silenciosa entre tela e banco.
      setItems(updated.icebreakers);
      onSaved(updated);
      return true;
    } catch (error) {
      setSaveError(
        errorMessage(error, "Não foi possível salvar as sugestões.")
      );
      return false;
    } finally {
      setSaving(false);
    }
  };
  useUnsavedChanges(dirty, save, "os quebra-gelos");

  return (
    <div className="card copiloto-icebreakers-card">
      <div className="card-title">
        <div><h3>Quebra-gelos</h3><p>Sugestões de pergunta mostradas quando a conversa da Mestra Help começa.</p></div>
      </div>
      <div className="copiloto-icebreaker-list">
        {items.map((text, i) => (
          <div key={i} className="copiloto-icebreaker-row">
            <input
              value={text}
              onChange={event => updateItem(i, event.target.value)}
              placeholder="Ex.: Qual é o índice CENA da minha empresa?"
            />
            <button type="button" className="icon-button" aria-label="Remover" onClick={() => removeItem(i)}>
              <Trash2 size={15}/>
            </button>
          </div>
        ))}
        {!items.length && <p className="copiloto-empty-list">Nenhuma sugestão cadastrada.</p>}
      </div>
      <div className="copiloto-admin-actions">
        <button type="button" className="secondary-button" onClick={addItem}>
          <Plus size={15}/> Adicionar pergunta
        </button>
        <button type="button" className="primary-button" disabled={!dirty || saving} onClick={() => void save()}>
          {saving ? <LoaderCircle className="spin" size={16}/> : <Check size={16}/>} Salvar
        </button>
      </div>
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
    </div>
  );
}

/**
 * A chave (só leitura — mora em Modelos de IA) e o MODELO do chat, que
 * é escolhido aqui entre os modelos Gemini ativos do catálogo, pelo id.
 * Até 2026-09-24 a chave `model` (nome) só era gravável por SQL: esta
 * aba mostrava o status e não oferecia a escolha.
 */
function CredentialsTab({
  token,
  status,
  settings,
  onSaved
}: {
  token: string;
  status: CopilotoStatus | null;
  settings: CopilotoSettings;
  onSaved: (settings: CopilotoSettings) => void;
}) {
  // `null` = ainda não respondeu. A falha tem estado PRÓPRIO: engolida
  // (`setModelos([])`), o seletor passava a dizer "Nenhum modelo Gemini
  // ativo no catálogo" — afirmação FALSA dada com a cara de um catálogo
  // realmente vazio — e o modelo já escolhido virava "fora do catálogo
  // ativo" (2026-10-09, teste de render).
  const [modelos, setModelos] = useState<AiModel[] | null>(null);
  const [erroModelos, setErroModelos] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setErroModelos(null);
    apiClient
      .listAiModels(token)
      .then(lista => setModelos(lista.filter(m => m.provider === "gemini" && m.is_active)))
      .catch(error =>
        setErroModelos(errorMessage(error, "Não foi possível carregar o catálogo de modelos."))
      );
  }, [token, tentativa]);

  const escolher = async (value: string) => {
    if (!value) return;
    setSaving(true);
    setSaveError(null);
    try {
      onSaved(await apiClient.updateCopilotoAdminSettings(token, { model_id: Number(value) }));
    } catch (error) {
      setSaveError(errorMessage(error, "Não foi possível salvar o modelo."));
    } finally {
      setSaving(false);
    }
  };

  const escolhido = settings.model_id;
  const lista = modelos ?? [];
  // Órfão só se afirma com o catálogo NA MÃO: sem resposta, não se sabe.
  const orfao = modelos !== null && escolhido !== null && !modelos.some(m => m.id === escolhido);

  return (
    <div className="card copiloto-credentials-card">
      <div className="card-title">
        <div><KeyRound size={18} aria-hidden="true"/><h3>Credenciais e modelo</h3></div>
      </div>
      <p>
        A Mestra Help fala só com o Gemini. A chave é cadastrada em Administração → Modelos
        de IA; o modelo do chat é escolhido aqui, entre os modelos Gemini ativos do catálogo.
      </p>
      <div className={`copiloto-credential-status ${status?.gemini_configured ? "is-ok" : "is-missing"}`}>
        <i aria-hidden="true"/>
        <div>
          <strong>Gemini</strong>
          <small>
            {status?.gemini_configured
              ? "Chave configurada"
              : "Chave não configurada — cadastre em Administração → Modelos de IA"}
          </small>
        </div>
      </div>
      {erroModelos ? (
        <ApiState kind="error" onRetry={() => setTentativa(n => n + 1)}>
          <span role="alert">{erroModelos}</span>
        </ApiState>
      ) : (
      <label className="field copiloto-model-pick">
        <span>Modelo do chat</span>
        <Select
          value={escolhido === null ? "" : String(escolhido)}
          disabled={saving || modelos === null}
          onChange={event => void escolher(event.target.value)}
        >
          <option value="" disabled>
            {modelos === null
              ? "Carregando modelos…"
              : modelos.length === 0
                ? "Nenhum modelo Gemini ativo no catálogo"
                : "Escolha um modelo"}
          </option>
          {orfao && (
            <option value={String(escolhido)}>#{escolhido} (fora do catálogo ativo)</option>
          )}
          {lista.map(modelo => (
            <option key={modelo.id} value={String(modelo.id)}>
              {modelo.label ? `${modelo.label} (${modelo.name})` : modelo.name}
            </option>
          ))}
        </Select>
      </label>
      )}
      {saveError && <p className="form-error" role="alert">{saveError}</p>}
    </div>
  );
}

export function CopilotoAdminSettings({ token }: { token: string }) {
  const [tab, setTab] = useState<Tab>("prompts");
  // Trocar de aba desmonta a anterior: com alteração não salva, pergunta antes.
  const confirmLeave = useConfirmLeave();
  const [settings, setSettings] = useState<CopilotoSettings | null>(null);
  const [status, setStatus] = useState<CopilotoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([apiClient.copilotoAdminSettings(token), apiClient.copilotoAdminStatus(token)])
      .then(([settingsResult, statusResult]) => {
        setSettings(settingsResult);
        setStatus(statusResult);
      })
      .catch(caught => {
        setError(errorMessage(caught, "Não foi possível carregar as configurações."));
      })
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Mestra Help"
        description="Os prompts, as sugestões iniciais e a integração de IA da Mestra Help."
      />

      {loading && (
        <div className="operational-state" role="status">
          <LoaderCircle className="spin" size={19}/>Carregando…
        </div>
      )}
      {!loading && (error || !settings) && (
        <p className="form-error" role="alert">{error ?? "Configurações indisponíveis."}</p>
      )}
      {!loading && settings && (
        <>
          <div className="copiloto-admin-tabs" role="tablist">
            <button role="tab" aria-selected={tab === "prompts"} className={tab === "prompts" ? "active" : ""} onClick={() => confirmLeave(() => setTab("prompts"))}>
              Prompts
            </button>
            <button role="tab" aria-selected={tab === "quebra-gelos"} className={tab === "quebra-gelos" ? "active" : ""} onClick={() => confirmLeave(() => setTab("quebra-gelos"))}>
              Quebra-gelos
            </button>
            <button role="tab" aria-selected={tab === "credenciais"} className={tab === "credenciais" ? "active" : ""} onClick={() => confirmLeave(() => setTab("credenciais"))}>
              Credenciais de IA
            </button>
          </div>
          {tab === "prompts" && <PromptsTab token={token} settings={settings} onSaved={setSettings}/>}
          {tab === "quebra-gelos" && <IcebreakersTab token={token} settings={settings} onSaved={setSettings}/>}
          {tab === "credenciais" && (
            <CredentialsTab token={token} status={status} settings={settings} onSaved={setSettings}/>
          )}
        </>
      )}
    </div>
  );
}
